import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiUnlock, FiBookOpen, FiHeart } = FiIcons;

const AccessGate = ({ onAccessGranted }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const VALID_CODES = [
    'jspscqfu59', // Primary code for campaigns
    'auth7or2025', // Backup code 1
    'pur3pose847', // Backup code 2
    'gosp2el619', // Backup code 3
    'call5ing293'  // Backup code 4
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate loading for better UX
    await new Promise(resolve => setTimeout(resolve, 800));

    if (VALID_CODES.includes(code.toLowerCase().trim())) {
      localStorage.setItem('planningWithPurpose_access', 'granted');
      localStorage.setItem('planningWithPurpose_accessDate', new Date().toISOString());
      onAccessGranted();
    } else {
      setError('Invalid access code. Please check your email for the correct code.');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-soft-blue/10 to-sage-green/20 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full border border-warm-brown/10"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="bg-gradient-to-r from-sage-green to-soft-blue rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4"
          >
            <SafeIcon icon={FiBookOpen} className="text-2xl text-white" />
          </motion.div>
          
          <h1 className="font-serif text-3xl text-warm-brown mb-2">
            Planning with Purpose
          </h1>
          <p className="text-warm-brown/70 mb-6">
            Plan your book with clarity, calling, and confidence
          </p>
          
          <div className="bg-sage-green/10 rounded-lg p-4 mb-6">
            <SafeIcon icon={FiHeart} className="text-sage-green mb-2 mx-auto" />
            <p className="text-sm text-warm-brown/80 italic">
              "For I know the plans I have for you," declares the Lord, "plans to prosper you and not to harm you, to give you hope and a future."
            </p>
            <p className="text-xs text-warm-brown/60 mt-1">- Jeremiah 29:11</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-warm-brown font-medium mb-2">
              <SafeIcon icon={FiLock} className="inline mr-2" />
              Enter Your Access Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter access code from your email"
              className="w-full px-4 py-3 rounded-xl border border-warm-brown/20 focus:border-sage-green focus:ring-2 focus:ring-sage-green/20 outline-none transition-all duration-200"
              disabled={isLoading}
            />
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm mt-2"
              >
                {error}
              </motion.p>
            )}
          </div>

          <motion.button
            type="submit"
            disabled={isLoading || !code.trim()}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-gradient-to-r from-sage-green to-soft-blue text-white py-3 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            ) : (
              <>
                <SafeIcon icon={FiUnlock} className="mr-2" />
                Begin Your Writing Journey
              </>
            )}
          </motion.button>
        </form>

        <div className="mt-8 pt-6 border-t border-warm-brown/10">
          <p className="text-xs text-warm-brown/60 text-center">
            Don't have an access code? Check your email or contact support.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AccessGate;